import { useEffect, useState } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { ButtonHeadless, LanguagesTabs, Spin } from '@wajeez-web/components/ui';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import {
  ComponentsActions,
  createComponentByTypeIdMultilingual,
  editComponentMultilingual,
  useContentSelector,
} from '@wajeez-web/stores/content';
import { AiFillDelete, AiFillPlusCircle, AiOutlineClose } from 'react-icons/ai';
import {
  FormInputController,
  WajeezFormInputV2,
} from '@wajeez-web/components/form-builder';
import {
  FormNumberInputV2,
  FormTextInputV2,
} from 'libs/components/form-builder/src/lib/InputsV2';
import { Server } from '@wajeez/wajeez.microservices';
import { Autocomplete } from '@wajeez-web/components/mui';
import { useContentDispatch } from '@wajeez-web/stores/content';
import { useParams } from 'react-router-dom';
import { BiLoaderAlt } from 'react-icons/bi';
import { Languages, languages } from '@wajeez-web/core/utils';

import { ConfirmationModal } from './ConfirmationModal';
import ReactRouterPrompt from 'react-router-prompt';
import { CloneModal } from './CloneModal';
import { SelectFactor } from './SelectFactor';
import { FormInputMultilingual } from './CollectionComponentFormBuilder';
import {
  useCreateComponentByTypeIdMultilingual,
  useEditComponentMultilingual,
} from '@wajeez-web/components/helpers';

type SortDirection = {
  id: number;
  name: string;
};

export const SORT_DIRECTION: SortDirection[] = [
  { id: 0, name: 'Asc' },
  { id: 1, name: 'Desc' },
];

export type SelectorOption = {
  value: string | undefined;
  label: string | undefined;
};

export type SelectorOptionsGrouped = {
  label: string | undefined;
  options: SelectorOption[];
};

export interface InputField {
  name: string | undefined;
  order: number | undefined;
  label: string | undefined;
  placeHolder: string | undefined;
  required: boolean | undefined;
  fieldType: Server.FieldType | undefined;
  fieldOptions: Server.ComponentTypeFieldOptionsOutput | undefined;
  permission: string | undefined;
}

export interface InputFieldWithSelectorOptions extends InputField {
  mappedFieldOptionsGrouped?: SelectorOptionsGrouped[];
  componentTypesOptions?: SelectorOption[];
  allowedPredefinedComponentsOptions?: SelectorOption[];
}

export interface InputFieldWithSelectorOptionsAndValues
  extends InputFieldWithSelectorOptions {
  value?: string | null | undefined;
}

export interface ComponentProfile {
  fields: InputFieldWithSelectorOptionsAndValues[] | undefined;
  languageId: Languages;
}

interface ComponentFormBuilderProps {
  type: 'Create' | 'Edit';
  dynamicFields: InputFieldWithSelectorOptionsAndValues[] | undefined;
  profiles: ComponentProfile[] | undefined;
  componentTypeId: number | undefined;
  onSubmit: () => void;
  onItemTemplateCloneSaveAndEdit: (itemTemplateId: string) => void;
  onItemTemplateEditButtonClick: (itemTemplateId: string) => void;
  itemTemplateField?: {
    name: string;
    label: string;
    selectOptions: { value: string | undefined; label: string | undefined }[];
    value?: string | undefined;
  };
  referenceName?: string | undefined;
  eventName?: string | undefined;
  pageNumber?: number | undefined;
  pageSize?: number | undefined;
  selectors?: Server.CollectionComponentContentEntitySelector[];
  sorters?: Server.CollectionComponentContentEntitySorter[];
  onCancel?: () => void;
}

export const ComponentFormBuilder = ({
  type,
  dynamicFields,
  profiles,
  componentTypeId,
  onSubmit,
  onItemTemplateCloneSaveAndEdit,
  onItemTemplateEditButtonClick,
  itemTemplateField,
  referenceName,
  eventName,
  pageNumber,
  pageSize,
  selectors,
  sorters,
  onCancel,
}: ComponentFormBuilderProps) => {
  const { componentId } = useParams<{
    flowId: string;
    pageId: string;
    componentId?: string;
  }>();

  const contentDispatch = useContentDispatch();

  const [canAddSelectFactor, setCanAddSelectFactor] = useState<boolean>(false);
  const [canAddSortFactor, setCanAddSortFactor] = useState<boolean>(false);
  const [notAddedSortFactors, setNotAddedSortFactors] = useState<
    Server.SortFactorOption[] | undefined
  >([]);
  const [isCancelConfirmationModalOpen, setIsCancelConfirmationModalOpen] =
    useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string>();
  const [isCloneComponentModalOpen, setIsCloneComponentModalOpen] =
    useState(false);
  const [
    isCloneComponentSaveAndEditSuccess,
    setIsCloneComponentSaveAndEditSuccess,
  ] = useState(false);
  const [forceNavigateOnSubmit, setForceNavigateOnSubmit] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Languages>(
    Languages.AR
  );
  const [addedLanguages, setAddedLanguages] = useState<Languages[]>();
  const [isLanguageTabChanging, setIsLanguageTabChanging] = useState(false);

  const selectedLanguage = languages.find((l) => l.value === selectedProfile);

  const selecSortFactors = useContentSelector(
    ({ contentEntityType }) => contentEntityType.selectSortFactors
  );

  const { status: createComponentByTypeIdStatus } =
    useCreateComponentByTypeIdMultilingual();
  const { status: editComponentStatus } = useEditComponentMultilingual();

  const cloneComponent = useContentSelector(
    ({ components }) => components.cloneComponent
  );

  const componentType = useContentSelector(
    ({ componentTypes }) => componentTypes.componentTypeById
  );
  const componentTypeData = componentType.data;

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isDirty },
    trigger,
    watch,
  } = useForm<FormInputMultilingual>({
    defaultValues: {
      itemTemplate: itemTemplateField?.value,
      referenceName,
      eventName,
      pageNumber,
      pageSize,
      // profiles: [],
      profiles:
        profiles?.map((p) => {
          return {
            fields:
              p.fields?.map((f) => {
                return {
                  name: f.name || '',
                  value: f.value || null,
                };
              }) || [],
            languageId: p.languageId,
          };
        }) || [],
      selectors: selectors?.map((s) => ({ ...s, id: Date.now() })) || [],
      sorters: sorters?.map((s) => ({ ...s, id: Date.now() })) || [],
    },
  });

  // watch((data) => {
  //   console.log(data);
  // });

  const {
    fields: profilesFormArray,
    remove: removeProfile,
    append: appendProfile,
  } = useFieldArray({
    control,
    name: 'profiles',
  });

  const {
    fields: selectFactorsFormArray,
    prepend: prependSelectFactor,
    remove: removeSelectFactor,
  } = useFieldArray({
    control,
    name: 'selectors',
  });

  const {
    fields: sortFactorsFormArray,
    prepend: prependSortFactor,
    remove: removeSortFactor,
  } = useFieldArray({
    control,
    name: 'sorters',
  });

  const onFormSubmit: SubmitHandler<FormInputMultilingual> = async (data) => {
    let mappedProfiles: {
      fields: {
        value: string | boolean | number[] | null;
        name: string;
      }[];
      languageId: Languages;
    }[];

    mappedProfiles = data.profiles.map((p) => {
      return {
        languageId: p.languageId,
        fields: p.fields.map((f) => {
          const foundDynamicField = dynamicFields?.find(
            (d) => d.name === f.name
          );

          if (foundDynamicField?.fieldType === Server.FieldType.MappedField) {
            const value = JSON.parse((f.value as string) || '[]') as
              | string[]
              | null;

            return {
              name: f.name,
              value: value?.map((v) => Number(v)) || null,
            };
          } else if (foundDynamicField?.fieldType === Server.FieldType.Switch) {
            return {
              ...f,
              value: f.value === 'True' || f.value === true ? true : false,
            };
          } else {
            return f;
          }
        }),
      };
    });

    const mappedSelectors = data.selectors.map((s) => {
      return {
        ...s,
        defaultCritiria: s.defaultCritiria,
        defaultEntityId: s.defaultEntityId || undefined,
        defaultTypeId: s.defaultTypeId ? Number(s.defaultTypeId) : undefined,
        defaultRelationLength: s.defaultRelationLength || undefined,
      };
    });

    const mappedSorters = data.sorters.map((s) => {
      return {
        ...s,
        direction: Number(s.direction),
      };
    });

    if (type === 'Create') {
      if (componentTypeId) {
        createComponentByTypeIdMultilingual(
          {
            componentTypeId,
            ...data,
            profiles: mappedProfiles,
            selectors: mappedSelectors,
            sorters: mappedSorters,
          },
          () => {
            setForceNavigateOnSubmit(true);
          }
        );
      }
    } else if (type === 'Edit') {
      if (componentId) {
        editComponentMultilingual(
          {
            id: componentId,
            ...data,
            profiles: mappedProfiles,
            selectors: mappedSelectors,
            sorters: mappedSorters,
          },
          () => {
            setForceNavigateOnSubmit(true);
          }
        );
      }
    }
  };

  const onItemTemplateCloneSuccess = () => {
    const allowedItemTemplateTypes =
      componentTypeData?.allowedItemTemplateTypes;

    allowedItemTemplateTypes &&
      contentDispatch(
        ComponentsActions.getComponentLookupsByComponentTypeIdsAsync({
          componentTypeIds: allowedItemTemplateTypes,
        })
      );
  };

  useEffect(() => {
    if (!componentId) {
      setAddedLanguages([Languages.AR]);
    } else {
      setAddedLanguages(
        profiles?.map((p) => {
          return p.languageId;
        }) as Languages[]
      );
    }
  }, [profiles?.length, componentId]);

  useEffect(() => {
    if (isCloneComponentSaveAndEditSuccess && cloneComponent.data) {
      setIsCloneComponentSaveAndEditSuccess(false);

      onItemTemplateCloneSaveAndEdit(cloneComponent.data);
    }
  }, [isCloneComponentSaveAndEditSuccess, cloneComponent.data]);

  useEffect(() => {
    if (forceNavigateOnSubmit) {
      onSubmit();
    }
  }, [forceNavigateOnSubmit]);

  useEffect(() => {
    setNotAddedSortFactors(
      selecSortFactors.data?.sortOptions?.filter((sf) => {
        return !sortFactorsFormArray.find((sffa) => sffa.factor === sf.factor);
      })
    );
  }, [sortFactorsFormArray]);

  const renderProfile = () => {
    const formProfiles = profilesFormArray;

    const selectedResponseProfileIndex = formProfiles.findIndex(
      (p) => p.languageId === selectedProfile
    );

    return formProfiles[selectedResponseProfileIndex]?.fields.map((f, i) => {
      const dynamicField = dynamicFields?.find((ff) => ff.name === f.name);

      const mapSelectOptions = () => {
        switch (dynamicField?.fieldType) {
          case Server.FieldType.MappedField:
            return dynamicField?.mappedFieldOptionsGrouped
              ?.map((g) => {
                const group = g.options.map((o) => {
                  return {
                    group: g.label,
                    value: o.value?.toString(),
                    label: o.label,
                  };
                });

                return [...group];
              })
              .flat();
          case Server.FieldType.RequiredComponent:
            return dynamicField?.componentTypesOptions;
          case Server.FieldType.PredefinedComponent:
            return dynamicField?.allowedPredefinedComponentsOptions;
          default:
            return [];
        }
      };

      return (
        <div className="mt-2">
          <WajeezFormInputV2
            control={control}
            name={`profiles.${selectedResponseProfileIndex}.fields.${i}.value`}
            fieldMeta={{
              ...dynamicField,
              fieldOptions: {
                ...dynamicField?.fieldOptions,
                selectOptions: mapSelectOptions(),
              },
              fieldType: dynamicField?.fieldType as any,
              label: dynamicField?.label,
            }}
            id={`profiles.${selectedResponseProfileIndex}.fields.${i}.value`}
          />
        </div>
      );
    });
  };

  const renderSelectFactors = () => {
    return selectFactorsFormArray.map((sf, i) => {
      const selectFactorTemplate = selecSortFactors.data?.selectOptions?.find(
        (d) => d.factor == sf.factor
      );

      return (
        <SelectFactor
          control={control}
          index={i}
          removeSelectFactor={removeSelectFactor}
          selectFactorTemplate={selectFactorTemplate}
        />
      );
    });
  };

  const renderSortFactors = () => {
    return sortFactorsFormArray.map((sf, i) => {
      const sortFactorTemplate = selecSortFactors.data?.sortOptions?.find(
        (s) => s.factor == sf.factor
      );

      return (
        <div
          key={sf.factor}
          className="mt-3 p-4 flex justify-center items-center rounded border"
        >
          <div className="w-full">
            <div className="w-full font-medium">{sortFactorTemplate?.name}</div>
            <div className="mt-4 w-full grid grid-cols-2 gap-10">
              <FormInputController
                control={control}
                name={`sorters.${i}.direction`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  required: true,
                  fieldOptions: {},
                }}
                renderInput={({ ...props }) => (
                  <Autocomplete
                    id={`sort-direction-autocomplete-${i}`}
                    options={SORT_DIRECTION.map((d) => {
                      return {
                        value: d.id,
                        label: d.name,
                      };
                    })}
                    label="Sort Direction"
                    color="#1f7983"
                    required
                    {...props}
                  />
                )}
              />
            </div>
          </div>
          <div
            className="mr-[20px] text-red-500 cursor-pointer hover:opacity-75 active:opacity-100"
            onClick={() => {
              removeSortFactor(i);
            }}
          >
            <AiFillDelete />
          </div>
        </div>
      );
    });
  };

  return (
    <div className="w-full">
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="w-full flex flex-col justify-start items-start"
        noValidate
      >
        <div className="w-full pb-6 border-b-[2px]">
          <FormSectionTitle sectionNumber={1} title="Basic Information" />
          <div className="w-[400px] flex flex-col justify-start items-start space-y-2">
            <FormTextInputV2
              control={control}
              name="referenceName"
              fieldMeta={{
                fieldType: Server.FieldType.Text,
                required: true,
                label: 'الاسم المرجعي',
                placeHolder: 'الاسم المرجعي',
                fieldOptions: {},
              }}
            />
            <FormTextInputV2
              control={control}
              name="eventName"
              fieldMeta={{
                fieldType: Server.FieldType.Text,
                required: true,
                label: 'اسم الحدث',
                placeHolder: 'اسم الحدث',
                fieldOptions: {},
              }}
            />
            <FormNumberInputV2
              control={control}
              name="pageNumber"
              fieldMeta={{
                fieldType: Server.FieldType.Number,
                label: 'رقم الصفحة',
                placeHolder: 'رقم الصفحة',
                fieldOptions: { min: 1 },
              }}
            />
            <FormNumberInputV2
              control={control}
              name="pageSize"
              fieldMeta={{
                fieldType: Server.FieldType.Number,
                label: 'حجم الصفحة',
                placeHolder: 'حجم الصفحة',
                fieldOptions: { min: 1 },
              }}
            />
            {itemTemplateField && itemTemplateField.selectOptions?.length && (
              <div className="mt-2 w-full flex justify-center items-end">
                <div className="w-full">
                  <FormInputController
                    control={control}
                    name={`itemTemplate`}
                    fieldMeta={{
                      required: true,
                      fieldType: Server.FieldType.Select,
                      fieldOptions: {},
                    }}
                    renderInput={({ ...props }) => (
                      <Autocomplete
                        id={`itemTemplate`}
                        options={
                          itemTemplateField?.selectOptions?.map((o) => {
                            return {
                              value: o.value,
                              label: o.label,
                            };
                          }) || []
                        }
                        label="Item Template"
                        color="#1f7983"
                        required={true}
                        {...props}
                      />
                    )}
                  />
                </div>
                <div className="rtl:mr-2 ltr:ml-2 w-[70px]">
                  <ButtonHeadless
                    paddingYStyle="py-1"
                    borderRadiusStyle="rounded-[6px]"
                    action={() => {
                      const itemTemplateId = getValues('itemTemplate');

                      itemTemplateId &&
                        onItemTemplateEditButtonClick(itemTemplateId);
                    }}
                  >
                    <div className="text-white text-[15px]">
                      <ReturnTranslatedText
                        text="buttons.Edit"
                        origintext="Edit"
                      />
                    </div>
                  </ButtonHeadless>
                </div>
                <div className="rtl:mr-2 ltr:ml-2 w-[70px]">
                  <ButtonHeadless
                    paddingYStyle="py-1"
                    borderRadiusStyle="rounded-[6px]"
                    action={() => {
                      setSelectedComponentId(getValues('itemTemplate'));
                      setIsCloneComponentModalOpen(true);
                    }}
                  >
                    <div className="text-white text-[15px]">
                      <ReturnTranslatedText
                        text="buttons.Clone"
                        origintext="Clone"
                      />
                    </div>
                  </ButtonHeadless>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-6 w-full pb-6 border-b-[2px]">
          <div className="w-full">
            <FormSectionTitle sectionNumber={2} title="Dynamic Fields" />
            <div className="mt-4">
              <LanguagesTabs
                languages={languages}
                selectedLanguageValue={selectedProfile}
                onSelect={(value) => {
                  setSelectedProfile(value);

                  const notSelectedProfileIndex = getValues(
                    'profiles'
                  ).findIndex((p) => p.languageId !== selectedLanguage?.value);

                  trigger(`profiles.${notSelectedProfileIndex}`);

                  if (value !== selectedProfile) {
                    setIsLanguageTabChanging(true);

                    setTimeout(() => {
                      setIsLanguageTabChanging(false);
                    }, 300);
                  }
                }}
                onAddedLanguagesChange={(languages, language) => {
                  const formProfileIndex = getValues('profiles').findIndex(
                    (p) => p.languageId === language
                  );

                  if (
                    addedLanguages &&
                    languages.length > addedLanguages.length
                  ) {
                    const defaultFields =
                      dynamicFields?.map((f) => {
                        return {
                          name: f.name || '',
                          value: null,
                        };
                      }) || [];

                    appendProfile({
                      languageId: language,
                      fields: defaultFields,
                    });
                  } else if (
                    addedLanguages &&
                    languages.length < addedLanguages.length &&
                    formProfileIndex !== -1
                  ) {
                    removeProfile(formProfileIndex);
                  }
                  setAddedLanguages(languages);
                }}
                addedLanguages={addedLanguages}
              />
            </div>
            <div
              className="px-4 pt-1 pb-6"
              style={{
                backgroundColor: selectedLanguage?.activeColor,
                color: selectedLanguage?.inactiveColor,
              }}
            >
              {!isLanguageTabChanging ? (
                <div className="w-[400px]">{renderProfile()}</div>
              ) : (
                <div className="w-full h-[300px] flex justify-center items-center">
                  <Spin colorStyle="text-currentcolor" />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-6 w-full pb-6 border-b-[2px]">
          <FormSectionTitle sectionNumber={3} title="Selectors" />
          <AddSelectorOrSorterButton
            type="Selector"
            isActive={!canAddSelectFactor}
            onClick={() => {
              setCanAddSelectFactor(true);
            }}
          />
          <div className="w-[600px]">
            {canAddSelectFactor && (
              <div className="relative mt-3 p-4 rounded border">
                <div className="w-[400px]">
                  <Autocomplete
                    id="current-select-factor"
                    options={
                      selecSortFactors.data?.selectOptions?.map((nssf) => {
                        return {
                          value: nssf.factor?.toString() || '',
                          label: nssf.name || '',
                        };
                      }) || []
                    }
                    onChange={(value) => {
                      if (value && typeof value === 'string') {
                        prependSelectFactor({
                          factor: parseInt(value),
                          id: Date.now(),
                        });

                        setCanAddSelectFactor(false);
                      }
                    }}
                    label="Selector"
                    color="#1f7983"
                  />
                </div>
                <Close
                  onClick={() => {
                    setCanAddSelectFactor(false);
                  }}
                />
              </div>
            )}
            {renderSelectFactors()}
          </div>
        </div>
        <div className="mt-6 w-[600px]">
          <FormSectionTitle sectionNumber={4} title="Sorters" />
          <AddSelectorOrSorterButton
            type="Sorter"
            isActive={!canAddSortFactor && Boolean(notAddedSortFactors?.length)}
            onClick={() => {
              setCanAddSortFactor(true);
            }}
          />
          {canAddSortFactor && (
            <div className="relative mt-3 p-4 rounded border">
              <div className="w-[400px]">
                <Autocomplete
                  id="current-sort-factor"
                  options={
                    notAddedSortFactors?.map((sf) => {
                      return {
                        value: sf.factor?.toString() || '',
                        label: sf.name || '',
                      };
                    }) || []
                  }
                  onChange={(value) => {
                    if (value && typeof value === 'string') {
                      prependSortFactor({
                        factor: parseInt(value),
                        id: Date.now(),
                      });

                      setCanAddSortFactor(false);
                    }
                  }}
                  label="Sorter"
                  color="#1f7983"
                />
              </div>
              <Close
                onClick={() => {
                  setCanAddSortFactor(false);
                }}
              />
            </div>
          )}
          {renderSortFactors()}
        </div>
        <div className="w-full flex justify-end items-center space-s-2">
          <div className="mt-10 w-[70px]">
            <ButtonHeadless
              bgStyle="bg-warmGray-300"
              paddingYStyle="py-1"
              borderRadiusStyle="rounded-[6px]"
              action={() => {
                setIsCancelConfirmationModalOpen(true);
              }}
            >
              <div className="text-warmGray-400 text-[16px] font-bold">
                <ReturnTranslatedText
                  origintext="Cancel"
                  text={`buttons.${'Cancel'}`}
                />
              </div>
            </ButtonHeadless>
          </div>
          <div className="mt-10 w-[70px]">
            <ButtonHeadless
              type="submit"
              paddingYStyle="py-1"
              borderRadiusStyle="rounded-[6px]"
            >
              <div className="flex justify-center items-center text-white text-[16px] font-bold">
                <ReturnTranslatedText
                  origintext="Save"
                  text={`buttons.${'Save'}`}
                />
                {(createComponentByTypeIdStatus === 'loading' ||
                  editComponentStatus === 'loading') && (
                  <BiLoaderAlt className="mr-1 animate-spin w-5" />
                )}
              </div>
            </ButtonHeadless>
          </div>
        </div>
      </form>
      {isCancelConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isCancelConfirmationModalOpen}
          onClose={() => {
            setIsCancelConfirmationModalOpen(false);
          }}
          onSubmit={() => {
            onCancel && onCancel();
          }}
          message="You didn't save the changes, are you sure that you want to cancel?"
        />
      )}
      {isCloneComponentModalOpen && (
        <CloneModal
          title="Clone Component"
          isOpen={isCloneComponentModalOpen}
          onClose={() => {
            setIsCloneComponentModalOpen(false);
          }}
          onSave={async (name) => {
            const res = await contentDispatch(
              ComponentsActions.cloneComponentAsync({
                referenceName: name,
                id: selectedComponentId,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              onItemTemplateCloneSuccess();

              setIsCloneComponentModalOpen(false);
            }
          }}
          onSaveAndEdit={async (name) => {
            const res = await contentDispatch(
              ComponentsActions.cloneComponentAsync({
                referenceName: name,
                id: selectedComponentId,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              // onItemTemplateCloneSuccess();

              setIsCloneComponentSaveAndEditSuccess(true);
            }
          }}
          isLoading={cloneComponent.status === 'loading'}
        />
      )}
      <ReactRouterPrompt when={isDirty && !forceNavigateOnSubmit}>
        {(props) => {
          const { isActive, onConfirm, onCancel } = props;

          return (
            <ConfirmationModal
              isOpen={isActive}
              onClose={onCancel}
              onSubmit={onConfirm}
              message="Are you sure that you want to leave this page?"
            />
          );
        }}
      </ReactRouterPrompt>
    </div>
  );
};

interface CloseProps {
  onClick: () => void;
}

const Close = ({ onClick }: CloseProps) => {
  return (
    <div
      className="absolute top-3 left-3 text-[14px] text-warmGray-500 cursor-pointer hover:opacity-75"
      onClick={onClick}
    >
      <AiOutlineClose />
    </div>
  );
};

interface AddSelectorOrSorterButtonProps {
  type: 'Selector' | 'Sorter';
  isActive: boolean;
  onClick: () => void;
}

const AddSelectorOrSorterButton = ({
  type,
  isActive,
  onClick,
}: AddSelectorOrSorterButtonProps) => {
  return (
    <div
      className={`w-fit mt-5 pt-1 flex justify-center items-center font-bold text-[16px] ${
        isActive
          ? 'text-primary cursor-pointer border-b border-transparent hover:border-primary hover:border-b'
          : 'text-gray-300 cursor-default'
      }`}
      onClick={() => {
        if (isActive) {
          onClick();
        }
      }}
    >
      <div className="rtl:ml-1 ltr:mr-1 text-[20px]">
        <AiFillPlusCircle />
      </div>
      <ReturnTranslatedText
        origintext={`Add ${type}`}
        text={`buttons.${`Add ${type}`}`}
      />
    </div>
  );
};

interface FormSectionTitleProps {
  sectionNumber: number;
  title: string;
}

const FormSectionTitle = ({ sectionNumber, title }: FormSectionTitleProps) => {
  return (
    <div className="mb-4 w-full flex justify-start items-center">
      <div className="w-5 h-5 flex justify-center items-center bg-[#212529] rounded-full text-white text-[15px] font-medium">
        {sectionNumber}
      </div>
      <div className="rtl:mr-2 ltr:ml-2 text-[19px] font-medium">
        <ReturnTranslatedText text={`general.${title}`} origintext={title} />
      </div>
    </div>
  );
};
